import React, { useEffect, useRef, useState } from 'react'
import { useParticipantsList } from './hooks'
import { InputRef, Table, Tooltip } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { CButton, CCardBody } from '@coreui/react-pro'
import { Participant } from './types'
import { getColumnSearchProps } from '../../../../libs/tableFunctions'
import { FilterValue } from 'antd/es/table/interface'
import {
  CheckCircleOutlined,
  CloseSquareOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons'
import { Link } from 'react-router-dom'
import { path } from '../../../../libs'
import { BanModal } from '../widgets/banModal/component'
import { getNumberFormat } from '../../../../libs/utils'
import { useUnit } from 'effector-react'
import {
  $isBanClientSuccess,
  setIsBanClientSuccess,
} from '../widgets/banModal/model'
import { Guard } from '../../../../features/session/guard'

export const ParticipantsList: React.FC = () => {
  const {
    participants,
    loading,
    totalCountOfParticipants,
    onChangeParticipantsList,
  } = useParticipantsList()
  const isBanClientSuccess = useUnit($isBanClientSuccess)

  const [modalData, setModalData] = useState({
    isOpen: false,
    id: '',
  })
  const [searchText, setSearchText] = useState<{
    [key: string]: string
  }>({})
  const [orderBy, setOrderBy] = useState('allTickets')
  const [orderDirection, setOrderDirection] = useState('-')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [filtersState, setFiltersState] = useState<
    Record<string, FilterValue | null>
  >({})

  const searchInput = useRef<InputRef>(null)

  const handleTableChange = ({
    currentPage,
    currentPageSize,
    currentOrderBy,
    currentOrderDirection,
    filters,
  }: {
    currentPage?: number
    currentPageSize?: number
    currentOrderBy?: string
    currentOrderDirection?: string
    filters?: Record<string, FilterValue | null>
  }) => {
    if (currentPage) {
      setPage(currentPage)
    }
    if (currentPageSize) {
      setPageSize(currentPageSize)
    }
    if (currentOrderBy) {
      setOrderBy(currentOrderBy)
    }
    if (currentOrderDirection) {
      if (currentOrderDirection == 'descend') {
        setOrderDirection('-')
      } else {
        setOrderDirection('')
      }
    }

    if (filters) {
      setFiltersState(filters)
    }
  }

  useEffect(() => {
    onChangeParticipantsList({
      limit: pageSize,
      page: page,
      orderBy: orderBy,
      orderDirection: orderDirection,
      searchText: searchText,
      filters: filtersState,
    })
  }, [pageSize, page, orderBy, orderDirection, searchText, filtersState])

  useEffect(() => {
    if (isBanClientSuccess) {
      onChangeParticipantsList({
        limit: pageSize,
        page: page,
        orderBy: orderBy,
        orderDirection: orderDirection,
        searchText: searchText,
        filters: filtersState,
      })
    }
  }, [isBanClientSuccess])

  const columns: ColumnsType<Participant> = [
    {
      title: 'ID',
      dataIndex: ['client', 'clientId'],
      ...getColumnSearchProps<Participant>(
        'client.clientId',
        setSearchText,
        searchText,
        searchInput,
      ),
      render: (id) => (
        <Link to={path.clients.details(id)} style={{ textDecoration: 'none' }}>
          {id}
        </Link>
      ),
    },
    {
      title: 'Name',
      dataIndex: ['client', 'fullName'],
      ...getColumnSearchProps<Participant>(
        'client.fullName',
        setSearchText,
        searchText,
        searchInput,
      ),
    },
    {
      title: 'Email',
      dataIndex: ['client', 'email'],
      ...getColumnSearchProps<Participant>(
        'client.email',
        setSearchText,
        searchText,
        searchInput,
      ),
    },
    {
      title: 'Is Partner',
      dataIndex: ['client', 'partner'],
      filters: [
        {
          text: 'Yes',
          value: true,
        },
        {
          text: 'No',
          value: false,
        },
      ],
      filterMultiple: false,
      render: (isPartner: boolean) => (
        <>
          {isPartner ? (
            <CheckCircleOutlined className={'text-success'} />
          ) : (
            <CloseSquareOutlined className={'text-danger'} />
          )}
        </>
      ),
    },
    {
      title: 'Tickets',
      sorter: true,
      key: 'allTickets',
      defaultSortOrder: 'descend',
      render: (item: Participant) => (
        <div>
          {item.allTickets}{' '}
          <Tooltip
            title={
              <div>
                <div>Trading: {item.ticketsCount}</div>
                <div>New client: {item.clientsFirstTicketsCount}</div>
                <div>Client trading: {item.clientsOverallTicketsCount}</div>
                <div>Task tickets: {item.taskTicketsCount}</div>
              </div>
            }
          >
            <InfoCircleOutlined />
          </Tooltip>
        </div>
      ),
    },
    {
      title: 'Coins',
      sorter: true,
      key: 'christmasTreeTotalPrice',
      render: (item: Participant) => (
        <div>
          {getNumberFormat(item.christmasTreeTotalPrice, 2)}{' '}
          <Tooltip
            title={
              <div>
                <div>All points: {item.allPoints}</div>
                <div>Spend points: {item.spentPoints}</div>
                <div>
                  Balance points: {getNumberFormat(item.balancePoints, 2)}
                </div>
              </div>
            }
          >
            <InfoCircleOutlined />
          </Tooltip>
        </div>
      ),
    },
    {
      title: 'Tasks',
      dataIndex: 'completedTasks',
      sorter: true,
    },
    {
      title: 'Lots',
      dataIndex: 'lots',
      sorter: true,
      render: (lots: string) => lots && getNumberFormat(lots, 2),
    },
    {
      title: 'Status',
      sorter: true,
      key: 'status',
      render: (item: Participant) => {
        return item.bannedAt ? item.banReason : item.status
      },
      filterMultiple: false,
      filters: [
        {
          text: 'active',
          value: 'active',
        },
        {
          text: 'ban',
          value: 'disqualified',
        },
      ],
    },
    {
      title: 'Actions',
      fixed: 'right',
      width: 150,
      render: (item: Participant) => {
        return (
          item.status == 'active' && (
            <Guard permission="new_year_2026_ban">
              <div className="d-flex flex-column gap-1">
                <CButton
                  color="danger"
                  onClick={() =>
                    setModalData((prev) => ({
                      ...prev,
                      isOpen: true,
                      id: String(item.client.clientId),
                    }))
                  }
                >
                  Disqualification
                </CButton>
              </div>
            </Guard>
          )
        )
      },
    },
  ]

  return (
    <CCardBody>
      <Table
        dataSource={participants}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        rowKey="participantsId"
        columns={columns}
        pagination={{
          showSizeChanger: true,
          total: totalCountOfParticipants,
        }}
        onChange={(pagination, filters, sorter) => {
          return handleTableChange({
            //@ts-ignore
            currentPage: pagination.current, //@ts-ignore
            currentPageSize: pagination.pageSize, //@ts-ignore
            currentOrderDirection: sorter.order, //@ts-ignore
            currentOrderBy: sorter.field || sorter.columnKey,
            filters,
          })
        }}
      />
      <BanModal
        id={modalData.id}
        isVisible={modalData.isOpen}
        onClose={() => {
          setModalData((prev) => ({
            ...prev,
            isOpen: false,
            id: '',
          }))
          setIsBanClientSuccess(false)
        }}
      />
    </CCardBody>
  )
}
